import { useState } from "react";
import { Mail, MessageSquare, Clock, AlertCircle, MapPinned } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SITE_CONFIG } from "@/config/site";

import envImg from "../assets/icon-envelope.svg";

// =============================================================================
// TODO: CONTACT FORM BACKEND INTEGRATION
// =============================================================================
// This contact form currently uses a mailto fallback.
// To integrate with a backend API:
// 
// 1. Create a backend endpoint (e.g., POST /api/contact)
// 2. Update handleSubmit to call the API instead of mailto
// 3. Handle success/error states appropriately
// 4. Consider adding rate limiting and spam protection (reCAPTCHA)
//
// Example API integration:
// const response = await fetch('/api/contact', {
//   method: 'POST',
//   headers: { 'Content-Type': 'application/json' },
//   body: JSON.stringify(formState)
// });
// =============================================================================

export const ContactPage = () => {
  const [formState, setFormState] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [errors, setErrors] = useState({});

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formState.name.trim()) {
      newErrors.name = "Name is required";
    }
    
    if (!formState.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formState.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    
    if (!formState.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formState.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // TODO: Replace with backend API call when available
    // For now, open mailto as fallback
    const mailtoLink = `mailto:${SITE_CONFIG.contact.supportEmail}?subject=${encodeURIComponent(formState.subject)}&body=${encodeURIComponent(`Name: ${formState.name}\nEmail: ${formState.email}\n\n${formState.message}`)}`;
    window.location.href = mailtoLink;
  };

  const handleInputChange = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <div className="pt-24 pb-20 inner-contact-page bg-black-v2">
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
        {/* <Badge className="section-badge mb-6">
          <MessageSquare className="w-3 h-3 text-violet-400" />
          <span>Contact</span>
        </Badge> */}
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mt-20 mb-6">
          Get in Touch
        </h1>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
          Have a question or need help? We're here to assist you.
        </p>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-12">


            <div className="mx-auto">
              <div className="p-6 rounded-2xl sm-box bg-violet-600/101 border border-violet-500/201 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-600/201 flex items-center justify-center flex-shrink-0">
                    <img src={envImg} className="w-51 h-51 text-violet-4001" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Need Help?</h3>
                    <p className="text-white text-sm mb-2">
                      For support, billing, or account help, email:
                    </p>
                    <a 
                      href={`mailto:${SITE_CONFIG.contact.supportEmail}`}
                      className="link font-medium text-lg"
                    >
                      {SITE_CONFIG.contact.supportEmail}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* <div>
               <div className="p-6 rounded-2xl sm-box bg-violet-600/101 border border-violet-500/201">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                    <img src={envImg} className="w-15 h-51" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Business Inquiries</h3>
                    <p className="white-text-500 text-sm mt-1  mb-2">
                      For partnerships and business-related questions.
                    </p>
                    <a 
                      href={`mailto:${SITE_CONFIG.contact.businessEmail}`}
                      className="link font-medium text-lg"
                    >
                      {SITE_CONFIG.contact.businessEmail}
                    </a>
                    
                  </div>
                </div>
              </div>
            </div> */}

            {/* <div>
              <div className="p-6 pb-6 rounded-2xl bg-violet-600/10 border border-violet-500/20 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Business Hours</h3>
                    <p className="text-gray-400">{SITE_CONFIG.contact.supportHours}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      We typically respond within 24-48 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="p-6 pb-6 rounded-2xl bg-violet-600/10 border border-violet-500/20 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-violet-600/20 flex items-center justify-center flex-shrink-0">
                    <MapPinned className="w-5 h-5 text-violet-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">Business Address</h3>
                    <p className="text-gray-400 xl:text-sm">{SITE_CONFIG.legal.businessAddress}</p>
                  </div>
                </div>
              </div>
            </div> */}
         

        </div>
      </div>

    </div>
  );
};

export default ContactPage;
